import { useState } from 'react';
import { IconButton, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useHavePermission } from '@/hooks';
import { ImageType, PointOfInterest } from '@/__generated__/graphql';
import UploadAttachmentsDialog from './UploadAttachmentsDialog';
import { AttachmentsViewer } from './AttachmentsViewer';
import { Permissions, ScreenshotLocationType } from '@/types';
import { Attachment } from '../types';
import { MediaActionButtons } from '@/components/shared/MediaActionButtons';
import { LocationTag } from '../../../../../../../constants';

type Props = {
  selectedPoiInfo: PointOfInterest;
  isUpdating: boolean;
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>;
  handleCaptureView: (location: ScreenshotLocationType) => Promise<void>;
};

export const MediaUploadsAndViewer = ({
  selectedPoiInfo,
  isUpdating,
  setIsUpdating,
  handleCaptureView,
}: Props) => {
  const [openAttachmentsDialog, setOpenAttachmentsDialog] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [remediationNote, setRemediationNote] = useState<string | undefined | null>(
    selectedPoiInfo.remediationNote
  );

  const isAddMode = selectedPoiInfo.files && selectedPoiInfo.files.length === 0;
  const dialogTitle = isAddMode ? 'Add Note' : 'Edit Note';
  const buttonText = isAddMode ? 'Add' : 'Update';
  const handleDialogToggle = () => setOpenAttachmentsDialog(!openAttachmentsDialog);
  const userCanUploadMedia = useHavePermission(Permissions.CREATE_UPLOAD_MEDIA);

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" my={2}>
        <Typography variant="h6"> Note</Typography>
        {!isAddMode && userCanUploadMedia && (
          <IconButton onClick={handleDialogToggle}>
            <EditIcon />
          </IconButton>
        )}
      </Stack>
      <Typography variant="caption">{remediationNote ?? ''}</Typography>
      <MediaActionButtons
        loading={isUpdating}
        onUploadClick={handleDialogToggle}
        onCaptureClick={() => handleCaptureView('attachments')}
        location={LocationTag.ViewerSelectedPoi}
      />
      <AttachmentsViewer
        attachments={attachments.filter(
          (attachment) => attachment.imageType !== ImageType.WhiteBgPdf || !attachment.imageType
        )}
        allAttachments={attachments}
        setAttachments={setAttachments}
        selectedPoiInfo={selectedPoiInfo}
        setIsUpdating={setIsUpdating}
        location={LocationTag.ViewerSelectedPoiOverview}
      />
      <UploadAttachmentsDialog
        openDialog={openAttachmentsDialog}
        setOpenDialog={setOpenAttachmentsDialog}
        pointOfInterest={selectedPoiInfo}
        attachments={[...attachments]}
        dialogTitle={dialogTitle}
        buttonText={buttonText}
        setAttachments={setAttachments}
        setIsUpdating={setIsUpdating}
        setRemediationNote={setRemediationNote}
      />
    </>
  );
};
